import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  LinearProgress
} from "@mui/material";
import {
  Save as SaveIcon,
  Create as SignIcon,
  ArrowBack as BackIcon,
  Clear as ClearIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  Description as FormIcon,
  MedicalInformation as MedicalIcon,
  AccessTime as DurationIcon,
  Contacts as ContactsIcon
} from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { toast } from "react-toastify";
import SignatureCanvas from "react-signature-canvas";
import { MEDICAL_BILLING_PERSON, secureApi } from "../config";
import { Context } from "../context";
import TopNav from "../components/topnav";

const useStyles = makeStyles((theme) => ({
  formContainer: {
    paddingTop: 20,
    paddingBottom: 40,
  },
  pageHeader: {
    marginBottom: 20,
    textAlign: "center",
  },
  formTitle: {
    marginBottom: 15,
    textAlign: "center",
    fontWeight: "500",
  },
  bold: {
    fontWeight: "bold",
  },
  underline: {
    textDecoration: "underline",
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: 12,
    marginTop: 15,
  },
  signatureBox: {
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: 10,
    minHeight: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  signatureImg: {
    maxHeight: "50px",
    maxWidth: "100%",
  },
  signatureSection: {
    marginTop: 20,
    marginBottom: 20,
  },
  actionButtons: {
    marginTop: 30,
  },
  fieldLabel: {
    fontWeight: "500",
    marginBottom: 8,
  },
  sectionCard: {
    marginBottom: 20,
  },
  listItem: {
    marginBottom: 8,
  },
  stepperContainer: {
    marginBottom: 30,
  },
  formGroup: {
    marginBottom: 16,
  }
}));

const today = new Date();
const year = today.getFullYear();
const month = String(today.getMonth() + 1).padStart(2, "0");
const date = String(today.getDate()).padStart(2, "0");
const formattedDate = `${month}/${date}/${year}`;

const HipaaForm = () => {
  const classes = useStyles();
  const { patient_id } = useParams();
  const navigate = useNavigate();
  const { setLoggedIn, user } = useContext(Context);

  useEffect(() => {
    if (user && user.role === MEDICAL_BILLING_PERSON) {
      toast.warn("You don't have permission to access this page.");
      navigate("/");
    }
  }, [user, navigate]);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState({});
  
  // Section I
  const [section1, setSection1] = useState("Modal Medical Services PLLC");
  
  // Section II
  const [section2check1, setSection2check1] = useState(false);
  const [section2check2, setSection2check2] = useState(false);
  const [section2check3, setSection2check3] = useState(false);
  
  // Section III
  const [section3, setSection3] = useState("Billing purposes, Attorney relevant information");
  
  // Section IV
  const [section4name, setSection4name] = useState("");
  const [section4organization, setSection4organization] = useState("Modal Medical Services PLLC");
  const [section4address, setSection4address] = useState("369 Lexington Ave 25h Floor, New York, NY 10016");
  
  // Section V
  const [section5check1, setSection5check1] = useState(false);
  const [section5from, setSection5from] = useState("");
  const [section5to, setSection5to] = useState("");
  const [section5check2, setSection5check2] = useState(false);
  const [section5check3, setSection5check3] = useState(false);
  const [section5, setSection5] = useState("");
  const [section5name, setSection5name] = useState("");
  const [section5organization, setSection5organization] = useState("Modal Medical Services PLLC");
  const [section5address, setSection5address] = useState("369 Lexington Ave 25th Floor, New York, NY 10016");
  
  // Section VI - Signature
  const [patientSignature, setPatientSignature] = useState("");
  const [patientSignatureDate, setPatientSignatureDate] = useState("");
  
  // Signature pad
  const [signPad, setSignPad] = useState({});
  const [signed, setSigned] = useState(false);
  const [signModalOpen, setSignModalOpen] = useState(false);

  const handleClickPatientSign = () => {
    setSignModalOpen(true);
  };
  
  const handleClear = () => {
    signPad.clear();
    setSigned(false);
  };

  const handleSignModalClose = (event, reason) => {
    if (reason !== "backdropClick") {
      handleClear();
      setSignModalOpen(false);
    }
  };

  const handleSign = () => {
    const physicalSign = signPad.getTrimmedCanvas().toDataURL("image/png");
    setPatientSignature(physicalSign);
    if (patientSignatureDate.trim() === "")
      setPatientSignatureDate(formattedDate);
    handleSignModalClose();
  };

  const handleSave = () => {
    setLoading(true);
    
    const formData = {
      patientName:
        patient &&
        patient.patientInfo &&
        `${patient.patientInfo.first_name} ${patient.patientInfo.last_name}`,
      section1,
      section2check1,
      section2check2,
      section2check3,
      section3,
      section4name,
      section4organization,
      section4address,
      section5check1,
      section5from,
      section5to,
      section5check2,
      section5check3,
      section5,
      section5name,
      section5organization,
      section5address,
      patientSignature,
      patientSignatureDate,
    };
    
    secureApi(localStorage.getItem("alex-med-token"))
      .post(`/hipaa-form/${patient_id}`, formData)
      .then(() => {
        toast.success("HIPAA form has been saved successfully!");
        navigate(`/patient/${patient_id}`);
      })
      .catch((e) => {
        console.log(e);
        if (e.response && e.response.status === 401) {
          toast.warn("Session has been expired. You need to login again!");
          setLoggedIn(false);
          navigate("/login");
        } else if (e.response) {
          toast.error(e.response.data.message);
        } else {
          toast.error("An error occurred while saving the form.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (patient_id) {
      setLoading(true);
      secureApi(localStorage.getItem("alex-med-token"))
        .get(`patient/${patient_id}`)
        .then((response) => {
          const { data: patient } = response;
          setPatient(patient);
          if (patient && patient.hipaaForm) {
            setSection1(patient.hipaaForm.section1 || "Modal Medical Services PLLC");
            setSection2check1(patient.hipaaForm.section2check1 || false);
            setSection2check2(patient.hipaaForm.section2check2 || false);
            setSection2check3(patient.hipaaForm.section2check3 || false);
            setSection3(patient.hipaaForm.section3 || "Billing purposes, Attorney relevant information");
            setSection4name(patient.hipaaForm.section4name || "");
            setSection4organization(patient.hipaaForm.section4organization || "Modal Medical Services PLLC");
            setSection4address(patient.hipaaForm.section4address || "369 Lexington Ave 25h Floor, New York, NY 10016");
            setSection5check1(patient.hipaaForm.section5check1 || false);
            setSection5from(patient.hipaaForm.section5from || "");
            setSection5to(patient.hipaaForm.section5to || "");
            setSection5check2(patient.hipaaForm.section5check2 || false);
            setSection5check3(patient.hipaaForm.section5check3 || false);
            setSection5(patient.hipaaForm.section5 || "");
            setSection5name(patient.hipaaForm.section5name || "");
            setSection5organization(patient.hipaaForm.section5organization || "Modal Medical Services PLLC");
            setSection5address(patient.hipaaForm.section5address || "369 Lexington Ave 25th Floor, New York, NY 10016");
            setPatientSignature(patient.hipaaForm.patientSignature || "");
            setPatientSignatureDate(patient.hipaaForm.patientSignatureDate || "");
          }
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else if (e.response) {
            toast.error(e.response.data.message);
          } else {
            toast.error("An error occurred while loading patient data.");
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      toast.warn("Invalid patient ID!");
      navigate("/");
    }
  }, [patient_id, navigate, setLoggedIn]);

  const steps = [
    'Basic Information',
    'Health Information',
    'Reason & Recipients',
    'Duration',
    'Signature'
  ];

  const getStepIcon = (index) => {
    const icons = [
      <PersonIcon />,
      <MedicalIcon />,
      <ContactsIcon />,
      <DurationIcon />,
      <SignIcon />
    ];
    return icons[index];
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getPatientName = () => {
    return patient && patient.patientInfo
      ? `${patient.patientInfo.first_name} ${patient.patientInfo.last_name}`
      : "";
  };

  const renderSection1 = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Basic Information
      </Typography>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Please complete all sections of this HIPAA release form. If any sections are left blank, 
              this form will be invalid and it will not be possible for your health information to be 
              shared as requested.
            </Typography>
          </Alert>
          
          <Typography variant="h6" className={classes.sectionTitle}>
            Section I
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            I, <strong>{getPatientName()}</strong>, give my permission for
          </Typography>
          
          <TextField
            label="Healthcare Organization"
            value={section1}
            onChange={(e) => setSection1(e.target.value)}
            fullWidth
            variant="outlined"
            margin="normal"
            sx={{ mb: 2 }}
          />
          
          <Typography variant="body1">
            to share the information listed in Section II of this document with
            the person(s) or organization(s) I have specified in Section IV of
            this document.
          </Typography>
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="flex-end">
        <Button 
          variant="contained" 
          onClick={handleNext}
          endIcon={<CheckIcon />}
        >
          Continue
        </Button>
      </Box>
    </>
  );

  const renderSection2 = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Health Information
      </Typography>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section II - Health Information
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            I would like to give the above healthcare organization permission to:
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={section2check1}
                  onChange={() => setSection2check1(!section2check1)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1">
                  Disclose my complete health record including, but not limited to, diagnoses,
                  lab test results, treatment, and billing records for all conditions.
                </Typography>
              }
            />
          </Box>
          
          <Typography variant="subtitle1" fontWeight="500" sx={{ mb: 1 }}>
            Form of Disclosure:
          </Typography>
          
          <Box sx={{ ml: 2 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={section2check2}
                  onChange={() => setSection2check2(!section2check2)}
                  color="primary"
                />
              }
              label="Electronic copy or access via a web-based portal"
              sx={{ mb: 1 }}
            />
            
            <FormControlLabel
              control={
                <Checkbox 
                  checked={section2check3}
                  onChange={() => setSection2check3(!section2check3)}
                  color="primary"
                />
              }
              label="Hard copy"
            />
          </Box>
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="space-between">
        <Button 
          variant="outlined" 
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        
        <Button 
          variant="contained" 
          onClick={handleNext}
          endIcon={<CheckIcon />}
        >
          Continue
        </Button>
      </Box>
    </>
  );

  const renderSection3and4 = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Reason & Recipients
      </Typography>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section III - Reason for Disclosure
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please detail the reasons why information is being shared. If you
            are initiating the request for sharing information and do not wish
            to list the reasons for sharing, write 'at my request'.
          </Typography>
          
          <TextField
            label="Reason for disclosure"
            multiline
            rows={3}
            value={section3}
            onChange={(e) => setSection3(e.target.value)}
            variant="outlined"
            fullWidth
          />
        </CardContent>
      </Card>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section IV - Who Can Receive My Health Information
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            I give authorization for the health information detailed in section
            II of this document to be shared with the following individual(s) or
            organization(s)
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                value={section4name}
                onChange={(e) => setSection4name(e.target.value)}
                variant="outlined"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Organization"
                value={section4organization}
                onChange={(e) => setSection4organization(e.target.value)}
                variant="outlined"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={section4address}
                onChange={(e) => setSection4address(e.target.value)}
                variant="outlined"
                fullWidth
              />
            </Grid>
          </Grid>
          
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2">
              I understand that the person(s)/organization(s) listed above may not
              be covered by state/federal rules governing privacy and security of
              data and may be permitted to further share the information that is
              provided to them.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="space-between">
        <Button 
          variant="outlined" 
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        
        <Button 
          variant="contained" 
          onClick={handleNext}
          endIcon={<CheckIcon />}
        >
          Continue
        </Button>
      </Box>
    </>
  );

  const renderSection5 = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Duration of Authorization
      </Typography>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section V - Duration of Authorization
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            This authorization to share my health information is valid:
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={section5check1}
                  onChange={() => setSection5check1(!section5check1)}
                  color="primary"
                />
              }
              label="a) From specific date range:"
            />
            
            {section5check1 && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="From Date"
                      value={section5from}
                      onChange={(e) => setSection5from(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="MM/DD/YYYY"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="To Date"
                      value={section5to}
                      onChange={(e) => setSection5to(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="MM/DD/YYYY"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={section5check2}
                  onChange={() => setSection5check2(!section5check2)}
                  color="primary"
                />
              }
              label="b) All past, present, and future periods"
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={section5check3}
                  onChange={() => setSection5check3(!section5check3)}
                  color="primary"
                />
              }
              label="c) The date of the signature in section VI until the following event:"
            />
            
            {section5check3 && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <TextField
                  label="Specify Event"
                  value={section5}
                  onChange={(e) => setSection5(e.target.value)}
                  variant="outlined"
                  fullWidth
                />
              </Box>
            )}
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            I understand that I am permitted to revoke this authorization to
            share my health data at any time and can do so by submitting a
            request in writing to:
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                value={section5name}
                onChange={(e) => setSection5name(e.target.value)}
                variant="outlined"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Organization"
                value={section5organization}
                onChange={(e) => setSection5organization(e.target.value)}
                variant="outlined"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={section5address}
                onChange={(e) => setSection5address(e.target.value)}
                variant="outlined"
                fullWidth
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="500" sx={{ mb: 1 }}>
              I understand that:
            </Typography>
            
            <ul>
              <li className={classes.listItem}>
                In the event that my information has already been shared by the
                time my authorization is revoked, it may be too late to cancel
                permission to share my health data.
              </li>
              <li className={classes.listItem}>
                I understand that I do not need to give any further permission for
                the information detailed in Section II to be shared with the
                person(s) or organization(s) listed in section IV.
              </li>
              <li className={classes.listItem}>
                I understand that the failure to sign/submit this authorization or
                the cancellation of this authorization will not prevent me from
                receiving any treatment or benefits I am entitled to receive,
                provided this information is not required to determine if I am
                eligible to receive those treatments or benefits or to pay for the
                services I receive.
              </li>
            </ul>
          </Box>
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="space-between">
        <Button 
          variant="outlined" 
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        
        <Button 
          variant="contained" 
          onClick={handleNext}
          endIcon={<CheckIcon />}
        >
          Continue to Signature
        </Button>
      </Box>
    </>
  );

  const renderSection6 = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Signature
      </Typography>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Section VI - Signature
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Please review all information before signing. By signing this form, you confirm that all 
              information provided is accurate and you authorize the sharing of your health information 
              as specified.
            </Typography>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Patient Name
              </Typography>
              <TextField
                value={getPatientName()}
                variant="outlined"
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Date
              </Typography>
              <TextField
                value={patientSignatureDate}
                onChange={(e) => setPatientSignatureDate(e.target.value)}
                variant="outlined"
                fullWidth
                size="small"
                placeholder="MM/DD/YYYY"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Signature
              </Typography>
              <Box 
                className={classes.signatureBox}
                onClick={handleClickPatientSign}
                sx={{ cursor: 'pointer' }}
              >
                {patientSignature ? (
                  <img
                    src={patientSignature}
                    alt="Patient signature"
                    className={classes.signatureImg}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Click here to sign
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="space-between" className={classes.actionButtons}>
        <Button 
          variant="outlined" 
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        
        <Box>
          <Button
            variant="outlined"
            color="error"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            endIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            Save Form
          </Button>
        </Box>
      </Box>
    </>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderSection1();
      case 1:
        return renderSection2();
      case 2:
        return renderSection3and4();
      case 3:
        return renderSection5();
      case 4:
        return renderSection6();
      default:
        return 'Unknown step';
    }
  };

  return (
    <>
      <TopNav />
      {loading && <LinearProgress />}
      <Container maxWidth="lg" className={classes.formContainer}>
        <Paper elevation={2} sx={{ p: 4 }}>
          <Typography variant="h4" className={classes.pageHeader}>
            HIPAA RELEASE FORM
          </Typography>
          
          <Box className={classes.stepperContainer}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel StepIconComponent={() => getStepIcon(index)}>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          
          {getStepContent(activeStep)}
        </Paper>
      </Container>
      
      <Dialog 
        open={signModalOpen} 
        onClose={handleSignModalClose} 
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PersonIcon sx={{ mr: 1 }} />
            Patient Signature
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please sign in the space below:
          </Typography>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "5px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <SignatureCanvas
                penColor="black"
                canvasProps={{ 
                  width: 600, 
                  height: 200, 
                  className: "sigCanvas" 
                }}
                ref={(ref) => {
                  setSignPad(ref);
                }}
                onEnd={() => setSigned(true)}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button 
            onClick={handleClear} 
            variant="outlined" 
            color="warning"
            startIcon={<ClearIcon />}
          >
            Clear
          </Button>
          <Button 
            onClick={handleSign} 
            variant="contained" 
            disabled={!signed}
            endIcon={<SignIcon />}
            color="primary"
          >
            Confirm Signature
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HipaaForm;